import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Search, Plus, Pencil, Trash2, ChevronLeft, ChevronRight, LayoutTemplate,
  MapPin, ImageOff, Hotel, Ticket, Image as ImageIcon,
  ImagePlus, Star, X, Link2, Sparkles, Plane, Train, Car, Coffee, Ship, HelpCircle,
  BookOpen, Navigation, Footprints, Bot,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, apiErrorMessage } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import KimKimAddonsTab from "./KimKimAddonsTab";
import GuidesTipsTab from "./GuidesTipsTab";
import TravelDocPreface from "@/pages/config/TravelDocPreface";
import AITransferRewriter from "./AITransferRewriter";
import ImagePicker from "@/components/media/ImagePicker";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (u) => (!u ? "" : String(u).startsWith("/api/") ? `${BACKEND}${u}` : u);

const LIMIT = 24;
const EMPTY = {
  name: "", event_type: "", description: "", location: "", type: "TD",
  activo: true, start_time: "", duration: "", images: [], notes: "",
  service_type: "", cities: [],
};

// Los 10 tipos de servicio (idéntico al backend). Cada uno con etiqueta ES,
// icono lucide y color de badge (bg + texto tailwind).
const SERVICE_TYPES = [
  { key: "alojamiento", label: "Alojamiento",  icon: Hotel,       color: "bg-blue-100 text-blue-800 border-blue-200" },
  { key: "vuelo",       label: "Vuelo",        icon: Plane,       color: "bg-sky-100 text-sky-800 border-sky-200" },
  { key: "tren",        label: "Tren",         icon: Train,       color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  { key: "transfer",    label: "Transfer",     icon: Car,         color: "bg-amber-100 text-amber-800 border-amber-200" },
  { key: "rental_car",  label: "Coche de alquiler", icon: Car,    color: "bg-orange-100 text-orange-800 border-orange-200" },
  { key: "actividad",   label: "Actividad",    icon: Ticket,      color: "bg-purple-100 text-purple-800 border-purple-200" },
  { key: "entradas",    label: "Entradas",     icon: Ticket,      color: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200" },
  { key: "free_day",    label: "Día libre",    icon: Coffee,      color: "bg-slate-100 text-slate-700 border-slate-200" },
  { key: "self_drive",  label: "Self Drive",   icon: Navigation,  color: "bg-teal-100 text-teal-800 border-teal-200" },
  { key: "self_guided", label: "Self Guided",  icon: Footprints,  color: "bg-lime-100 text-lime-800 border-lime-200" },
  { key: "otros",       label: "Otros",        icon: Ship,        color: "bg-rose-100 text-rose-800 border-rose-200" },
];
// Categorías editoriales (idénticas a las de GuidesTipsTab). Se usan para
// permitir convertir un TD → Guía desde el diálogo de edición.
const EDITORIAL_CATEGORIES = [
  { key: "city_guide",     label: "Guías de ciudad" },
  { key: "restaurants",    label: "Restaurantes" },
  { key: "bars_nightlife", label: "Bares y Nightlife" },
  { key: "cafes_bakeries", label: "Cafés y panaderías" },
  { key: "gastronomy",     label: "Gastronomía" },
  { key: "cultural",       label: "Cultura y tradiciones" },
  { key: "tips",           label: "Consejos" },
  { key: "general_info",   label: "Información general" },
  { key: "disclaimer",     label: "Disclaimers / Legales" },
];

const SERVICE_TYPE_META = Object.fromEntries(SERVICE_TYPES.map((s) => [s.key, s]));

const stripHtml = (s) => (s || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

// Mapea ?view=... del sidebar a la pestaña activa del componente Plantillas.
// El tab "biblioteca" ahora es SOLO para TD (los TP están ocultos por decisión
// del usuario en feb-2026). `biblioteca-tp` y `catalogo-cotizador` mantenidos
// como alias para no romper enlaces existentes → caen al primer tab visible.
const VIEW_TO_TAB = {
  "biblioteca-tp": "biblioteca",
  "biblioteca-td": "biblioteca",
  "catalogo-cotizador": "kimkim-addons",
  "kimkim-addons": "kimkim-addons",
  "kimkim-hoteles-sin-match": "kimkim-hoteles-sin-match",
  "guides-tips": "guides-tips",
};

export default function PlantillasPage() {
  const qc = useQueryClient();
  const location_hook = useLocation();
  const navigate = useNavigate();
  const searchView = new URLSearchParams(location_hook.search).get("view") || "";
  const [activeTab, setActiveTab] = useState(VIEW_TO_TAB[searchView] || "kimkim-addons");
  useEffect(() => {
    const v = new URLSearchParams(location_hook.search).get("view") || "";
    if (VIEW_TO_TAB[v] && VIEW_TO_TAB[v] !== activeTab) setActiveTab(VIEW_TO_TAB[v]);
  }, [location_hook.search, activeTab]);
  const [term, setTerm] = useState("");
  const [q, setQ] = useState("");
  // TP oculto por decisión del usuario (feb-2026). La biblioteca solo muestra
  // plantillas TD; el switcher TP/TD se elimina del filtro.
  const [type] = useState("TD");
  // `event_type` (categoría legacy de Travefy) sigue existiendo en la BD para
  // matching interno, pero se oculta de la UI para evitar confusión con el
  // nuevo `service_type` (los 8 tipos operativos).
  const [eventType] = useState("all");
  const [serviceType, setServiceType] = useState("all");
  const [location, setLocation] = useState("all");
  const [activo, setActivo] = useState("all");
  const [page, setPage] = useState(0);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [imgIdx, setImgIdx] = useState(0);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [showUrls, setShowUrls] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [classifyOpen, setClassifyOpen] = useState(false);
  const [classifyAll, setClassifyAll] = useState(false);
  const [rewriterOpen, setRewriterOpen] = useState(false);
  const [cityInput, setCityInput] = useState("");
  const [classifyingIds, setClassifyingIds] = useState({});
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertCategory, setConvertCategory] = useState("city_guide");

  useEffect(() => {
    const t = setTimeout(() => { setQ(term); setPage(0); }, 350);
    return () => clearTimeout(t);
  }, [term]);

  const metaQ = useQuery({ queryKey: ["plantillas-meta"], queryFn: async () => (await api.get("/plantillas/meta")).data });
  const listQ = useQuery({
    queryKey: ["plantillas", q, eventType, type, serviceType, location, activo, page],
    queryFn: async () => (await api.get("/plantillas", { params: {
      q,
      event_type: eventType === "all" ? "" : eventType,
      type: type === "all" ? "" : type,
      service_type: serviceType === "all" ? "" : serviceType,
      location: location === "all" ? "" : location,
      activo: activo === "all" ? "" : activo,
      skip: page * LIMIT, limit: LIMIT,
    } })).data,
    keepPreviousData: true,
  });

  // Polling del job de clasificación mientras el modal está abierto o el job está corriendo.
  const jobQ = useQuery({
    queryKey: ["plantillas-classify-status"],
    queryFn: async () => (await api.get("/plantillas/classify/status")).data,
    refetchInterval: (data) => (data?.status === "running" ? 3000 : false),
    refetchIntervalInBackground: false,
  });
  const jobRunning = jobQ.data?.status === "running";

  const items = listQ.data?.items || [];
  const total = listQ.data?.total || 0;
  const pages = Math.max(1, Math.ceil(total / LIMIT));
  const meta = metaQ.data || {
    by_type: {}, event_types: [], locations: [], activos: 0, total: 0,
    by_service_type: {}, sin_clasificar: 0, td_total: 0, cities_top: [],
  };

  const stats = useMemo(() => ([
    { key: "TD", label: "Travel Document (TD)", value: meta.td_total ?? meta.by_type?.TD ?? 0, active: true, onClick: () => setPage(0) },
    { key: "activos", label: "Activas", value: meta.activos ?? 0, active: activo === "true", onClick: () => { setActivo(activo === "true" ? "all" : "true"); setPage(0); } },
    { key: "sin_clasificar", label: "Sin clasificar", value: meta.sin_clasificar ?? 0, active: serviceType === "sin_clasificar", onClick: () => { setServiceType(serviceType === "sin_clasificar" ? "all" : "sin_clasificar"); setPage(0); } },
    { key: "clasificadas", label: "Clasificadas (AI)", value: Math.max(0, (meta.td_total ?? 0) - (meta.sin_clasificar ?? 0)), active: false, onClick: () => { setServiceType("all"); setPage(0); } },
  ]), [meta, activo, serviceType]);

  const classifyMut = useMutation({
    mutationFn: () => api.post("/plantillas/classify/start", null, { params: { only_missing: !classifyAll } }),
    onSuccess: () => {
      toast.success(classifyAll ? "Reclasificación total iniciada." : "Clasificación AI iniciada. Puede tardar varios minutos.");
      qc.invalidateQueries({ queryKey: ["plantillas-classify-status"] });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  // Reclasificación de UNA sola plantilla (sync, ~5s con Claude).
  const classifyOneMut = useMutation({
    mutationFn: (pid) => api.post(`/plantillas/${pid}/classify`),
    onMutate: (pid) => setClassifyingIds((s) => ({ ...s, [pid]: true })),
    onSuccess: (res, pid) => {
      toast.success(`Reclasificada como ${res.data?.service_type || "?"}`);
      qc.invalidateQueries({ queryKey: ["plantillas"] });
      qc.invalidateQueries({ queryKey: ["plantillas-meta"] });
      setClassifyingIds((s) => { const { [pid]: _, ...rest } = s; return rest; });
      // Si estamos editando esta plantilla, sincroniza el formulario en vivo.
      if (editing?.id === pid) {
        setForm((f) => ({ ...f, service_type: res.data?.service_type || "", cities: res.data?.cities || [] }));
      }
    },
    onError: (e, pid) => {
      toast.error(apiErrorMessage(e));
      setClassifyingIds((s) => { const { [pid]: _, ...rest } = s; return rest; });
    },
  });

  // Convertir TD → Guía editorial. Requiere editorial_category.
  const convertMut = useMutation({
    mutationFn: () => api.post(`/plantillas/${editing?.id}/convert`, {
      to: "editorial",
      editorial_category: convertCategory,
    }),
    onSuccess: () => {
      toast.success("Plantilla convertida a Guía & Consejo.");
      qc.invalidateQueries({ queryKey: ["plantillas"] });
      qc.invalidateQueries({ queryKey: ["plantillas-meta"] });
      qc.invalidateQueries({ queryKey: ["editorial-summary"] });
      qc.invalidateQueries({ queryKey: ["editorial-list"] });
      setConvertOpen(false);
      setOpen(false);  // cerrar el diálogo de edición TD
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  // Cada vez que avanza el batch (processed cambia), refrescamos meta+lista para
  // ver las nuevas plantillas clasificadas sin cerrar el modal.
  const processed = jobQ.data?.processed ?? 0;
  const jobStatus = jobQ.data?.status;
  useEffect(() => {
    if (processed > 0) {
      qc.invalidateQueries({ queryKey: ["plantillas-meta"] });
      qc.invalidateQueries({ queryKey: ["plantillas"] });
    }
  }, [processed, qc]);
  useEffect(() => {
    if (jobStatus === "done") {
      toast.success("Clasificación AI completada.");
      qc.invalidateQueries({ queryKey: ["plantillas-meta"] });
      qc.invalidateQueries({ queryKey: ["plantillas"] });
    }
  }, [jobStatus, qc]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setImgIdx(0); setCityInput(""); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...EMPTY, ...p, images: p.images || [], cities: p.cities || [] }); setImgIdx(0); setCityInput(""); setOpen(true); };
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const payload = () => {
    // Si hay una ciudad todavía en el input, la incluimos automáticamente al guardar.
    const pendingCity = cityInput.trim();
    const cities = pendingCity && !(form.cities || []).includes(pendingCity)
      ? [...(form.cities || []), pendingCity]
      : (form.cities || []);
    return {
      ...form,
      cities,
      service_type: form.service_type || null,
      images: (typeof form._imagesText === "string"
        ? form._imagesText.split("\n").map((u) => u.trim()).filter(Boolean)
        : form.images) || [],
    };
  };

  const saveMut = useMutation({
    mutationFn: () => editing ? api.put(`/plantillas/${editing.id}`, payload()) : api.post("/plantillas", payload()),
    onSuccess: () => {
      toast.success(editing ? "Plantilla actualizada" : "Plantilla creada");
      qc.invalidateQueries({ queryKey: ["plantillas"] });
      qc.invalidateQueries({ queryKey: ["plantillas-meta"] });
      setOpen(false);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const delMut = useMutation({
    mutationFn: (id) => api.delete(`/plantillas/${id}`),
    onSuccess: () => {
      toast.success("Plantilla eliminada");
      qc.invalidateQueries({ queryKey: ["plantillas"] });
      qc.invalidateQueries({ queryKey: ["plantillas-meta"] });
      setDelTarget(null);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const dialogImages = (form.images || []);
  const setImages = (imgs) => setForm((s) => ({ ...s, images: imgs, _imagesText: imgs.join("\n") }));
  const addImage = (url) => { if (url && !(form.images || []).includes(url)) setImages([...(form.images || []), url]); };
  const removeImage = (url) => { setImages((form.images || []).filter((u) => u !== url)); setImgIdx(0); };
  const makeCover = (url) => { setImages([url, ...(form.images || []).filter((u) => u !== url)]); setImgIdx(0); };

  return (
    <div className="space-y-4" data-testid="plantillas-page">
      <div className="flex flex-col gap-1">
        <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
          <LayoutTemplate className="h-6 w-6 text-primary" /> Plantillas
        </h1>
        <p className="text-sm text-muted-foreground">
          Biblioteca de descripciones para <b>Travel Documents</b>, <b>Guías & Consejos</b> y <b>Addons KimKim</b>.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList data-testid="plantillas-tabs">
          <TabsTrigger value="kimkim-addons" data-testid="tab-kimkim">Addons KimKim</TabsTrigger>
          <TabsTrigger value="biblioteca" data-testid="tab-biblioteca">Travel Document (TD)</TabsTrigger>
          <TabsTrigger value="guides-tips" data-testid="tab-guides-tips">Guías & Consejos</TabsTrigger>
          <TabsTrigger value="td-preface" data-testid="tab-td-preface">Prefacio TD</TabsTrigger>
        </TabsList>
        <TabsContent value="biblioteca" className="space-y-4">

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {stats.map((s) => (
          <Card key={s.key} className="cursor-pointer transition-colors hover:bg-muted/50" onClick={s.onClick} data-testid={`plantilla-stat-${s.key}`}>
            <CardContent className={`p-3 ${s.active ? "text-primary" : ""}`}>
              <p className="text-xl font-bold leading-none tabular-nums">{s.value}</p>
              <p className="mt-1 text-[11px] text-muted-foreground">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {jobRunning && (
        <Card className="border-primary/40 bg-primary/5" data-testid="classify-progress-inline">
          <CardContent className="flex items-center gap-3 p-3">
            <Sparkles className="h-5 w-5 shrink-0 animate-pulse text-primary" />
            <div className="flex-1 space-y-1">
              <div className="flex items-center justify-between text-xs font-medium">
                <span>Clasificación AI en curso · {jobQ.data?.processed ?? 0} / {jobQ.data?.total ?? 0}</span>
                <span className="text-muted-foreground">{jobQ.data?.updated ?? 0} actualizadas · {jobQ.data?.errors ?? 0} errores</span>
              </div>
              <Progress value={jobQ.data?.total ? ((jobQ.data.processed / jobQ.data.total) * 100) : 0} className="h-1.5" />
            </div>
            <Button size="sm" variant="ghost" onClick={() => setClassifyOpen(true)} data-testid="classify-inline-detail">Detalle</Button>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:flex-wrap">
          <div className="relative sm:max-w-xs">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Buscar nombre, ciudad, descripción…" className="pl-8" data-testid="plantilla-search" />
          </div>
          <Select value={serviceType} onValueChange={(v) => { setServiceType(v); setPage(0); }}>
            <SelectTrigger className="sm:w-52" data-testid="plantilla-service-type-filter"><SelectValue placeholder="Tipo de servicio" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              <SelectItem value="sin_clasificar" data-testid="filter-service-type-sin-clasificar">Sin clasificar ({meta.sin_clasificar ?? 0})</SelectItem>
              {SERVICE_TYPES.map((s) => (
                <SelectItem key={s.key} value={s.key} data-testid={`filter-service-type-${s.key}`}>
                  {s.label} ({meta.by_service_type?.[s.key] ?? 0})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={location} onValueChange={(v) => { setLocation(v); setPage(0); }}>
            <SelectTrigger className="sm:w-44" data-testid="plantilla-location-filter"><SelectValue placeholder="Ubicación" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">Todas las ubicaciones</SelectItem>
              {(meta.locations || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setClassifyOpen(true)}
            disabled={jobRunning}
            data-testid="open-classify-dialog-button"
          >
            <Sparkles className="mr-1.5 h-4 w-4" />
            {jobRunning ? "Clasificando…" : "Auto-clasificar con AI"}
          </Button>
          <Button
            variant="outline"
            onClick={() => setRewriterOpen(true)}
            data-testid="open-ai-rewriter-button"
            title="Reescribe descripciones de transfer para usar {{start_location}} y {{end_location}}"
          >
            <Bot className="mr-1.5 h-4 w-4" />
            AI Rewrite Transfers
          </Button>
          <Button onClick={openNew} data-testid="new-plantilla-button"><Plus className="mr-1.5 h-4 w-4" />Nueva plantilla</Button>
        </div>
      </div>

      {listQ.isLoading ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-64 w-full rounded-xl" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed p-12 text-center" data-testid="plantillas-empty">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent text-accent-foreground"><LayoutTemplate className="h-6 w-6" /></div>
          <p className="font-semibold">Sin plantillas</p>
          <p className="text-sm text-muted-foreground">Ajusta los filtros o crea una nueva.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="plantillas-grid">
          {items.map((p) => {
            const cover = p.images?.[0];
            return (
              <Card key={p.id} className="group flex flex-col overflow-hidden transition-shadow hover:shadow-md" data-testid={`plantilla-card-${p.id}`}>
                <div className="relative h-36 w-full overflow-hidden bg-muted">
                  {cover ? (
                    <img src={cover} alt={p.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                      onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-muted-foreground"><ImageOff className="h-8 w-8" /></div>
                  )}
                  <div className="absolute left-2 top-2 flex flex-wrap gap-1">
                    {p.service_type && SERVICE_TYPE_META[p.service_type] && (() => {
                      const meta = SERVICE_TYPE_META[p.service_type];
                      const ServiceIcon = meta.icon;
                      return (
                        <Badge className={`gap-1 text-[10px] ${meta.color}`} data-testid={`card-service-type-${p.id}`}>
                          <ServiceIcon className="h-3 w-3" />{meta.label}
                        </Badge>
                      );
                    })()}
                    {!p.service_type && (
                      <Badge variant="outline" className="gap-1 border-dashed bg-background/80 text-[10px] text-muted-foreground" data-testid={`card-service-type-missing-${p.id}`}>
                        <HelpCircle className="h-3 w-3" />Sin clasificar
                      </Badge>
                    )}
                    {!p.activo && <Badge variant="destructive" className="text-[10px]">Inactiva</Badge>}
                  </div>
                  {p.images?.length > 1 && (
                    <Badge variant="outline" className="absolute bottom-2 right-2 gap-1 bg-background/80 text-[10px]"><ImageIcon className="h-3 w-3" />{p.images.length}</Badge>
                  )}
                </div>
                <CardContent className="flex flex-1 flex-col gap-1.5 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <p className="line-clamp-2 text-sm font-semibold leading-snug" title={p.name}>{p.name || "—"}</p>
                  </div>
                  {p.location && (
                    <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                      <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{p.location}</span>
                    </div>
                  )}
                  {p.cities?.length > 0 && (
                    <div className="flex flex-wrap gap-1" data-testid={`card-cities-${p.id}`}>
                      {p.cities.slice(0, 3).map((c) => (
                        <Badge key={c} variant="secondary" className="gap-1 text-[10px]"><MapPin className="h-2.5 w-2.5" />{c}</Badge>
                      ))}
                    </div>
                  )}
                  <p className="line-clamp-3 flex-1 text-xs text-muted-foreground">{stripHtml(p.description) || "Sin descripción."}</p>
                  <div className="mt-1 flex justify-end gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-primary"
                      onClick={() => classifyOneMut.mutate(p.id)}
                      disabled={!!classifyingIds[p.id]}
                      data-testid={`reclassify-plantilla-${p.id}`}
                      aria-label="Reclasificar con AI"
                      title="Reclasificar con AI"
                    >
                      <Sparkles className={`h-4 w-4 ${classifyingIds[p.id] ? "animate-pulse" : ""}`} />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(p)} data-testid={`edit-plantilla-${p.id}`} aria-label="Editar"><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => setDelTarget(p)} data-testid={`delete-plantilla-${p.id}`} aria-label="Eliminar"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" disabled={page <= 0} onClick={() => setPage((p) => p - 1)} data-testid="plantillas-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <span>Pág. {page + 1} / {pages}</span>
          <Button size="sm" variant="outline" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)} data-testid="plantillas-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
        <span data-testid="plantillas-count">{total} plantillas</span>
      </div>
        </TabsContent>
        <TabsContent value="kimkim-addons">
          <KimKimAddonsTab />
        </TabsContent>
        <TabsContent value="guides-tips">
          <GuidesTipsTab />
        </TabsContent>
        <TabsContent value="td-preface">
          <TravelDocPreface />
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="plantilla-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar plantilla" : "Nueva plantilla"}</DialogTitle>
            <DialogDescription>Descripción de venta e imágenes para el itinerario del cliente.</DialogDescription>
          </DialogHeader>

          {dialogImages.length > 0 && (
            <div className="space-y-2">
              <div className="relative h-56 w-full overflow-hidden rounded-lg bg-muted">
                <img src={dialogImages[imgIdx]} alt="preview" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.opacity = 0.2; }} />
                {dialogImages.length > 1 && (
                  <>
                    <Button size="icon" variant="secondary" className="absolute left-2 top-1/2 h-8 w-8 -translate-y-1/2" onClick={() => setImgIdx((i) => (i - 1 + dialogImages.length) % dialogImages.length)}><ChevronLeft className="h-4 w-4" /></Button>
                    <Button size="icon" variant="secondary" className="absolute right-2 top-1/2 h-8 w-8 -translate-y-1/2" onClick={() => setImgIdx((i) => (i + 1) % dialogImages.length)}><ChevronRight className="h-4 w-4" /></Button>
                    <Badge variant="outline" className="absolute bottom-2 right-2 bg-background/80 text-[10px]">{imgIdx + 1}/{dialogImages.length}</Badge>
                  </>
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2"><Label>Nombre *</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="plantilla-name-input" /></div>
            <div className="space-y-1.5"><Label>Ubicación</Label><Input value={form.location || ""} onChange={(e) => set("location", e.target.value)} data-testid="plantilla-location-input" /></div>
            <div className="flex items-center gap-2 pt-6">
              <Switch checked={!!form.activo} onCheckedChange={(v) => set("activo", v)} data-testid="plantilla-activo-switch" />
              <Label>Activa</Label>
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5">
                Tipo de servicio
                {editing?.classified_by === "ai" && (
                  <Badge variant="outline" className="gap-1 text-[9px]"><Sparkles className="h-2.5 w-2.5" />AI</Badge>
                )}
              </Label>
              <div className="flex gap-1.5">
                <Select value={form.service_type || "__none"} onValueChange={(v) => set("service_type", v === "__none" ? "" : v)}>
                  <SelectTrigger data-testid="plantilla-service-type-input"><SelectValue placeholder="Sin clasificar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">Sin clasificar</SelectItem>
                    {SERVICE_TYPES.map((s) => (
                      <SelectItem key={s.key} value={s.key} data-testid={`plantilla-service-type-option-${s.key}`}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {editing?.id && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => classifyOneMut.mutate(editing.id)}
                    disabled={!!classifyingIds[editing.id]}
                    data-testid="dialog-reclassify-button"
                    title="Reclasificar con AI"
                  >
                    <Sparkles className={`h-4 w-4 ${classifyingIds[editing.id] ? "animate-pulse text-primary" : ""}`} />
                  </Button>
                )}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Ciudades (chips)</Label>
              <div className="flex flex-wrap items-center gap-1 rounded-md border border-input bg-background px-2 py-1.5">
                {(form.cities || []).map((c) => (
                  <Badge key={c} variant="secondary" className="gap-1 text-[11px]" data-testid={`plantilla-city-chip-${c}`}>
                    {c}
                    <button
                      type="button"
                      className="ml-0.5 rounded-sm hover:bg-muted-foreground/20"
                      onClick={() => set("cities", (form.cities || []).filter((x) => x !== c))}
                      aria-label={`Quitar ${c}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
                <input
                  type="text"
                  className="min-w-[80px] flex-1 border-0 bg-transparent p-0 text-sm outline-none placeholder:text-muted-foreground"
                  placeholder={form.cities?.length ? "" : "Ej: Madrid, Rome"}
                  value={cityInput}
                  onChange={(e) => setCityInput(e.target.value)}
                  onKeyDown={(e) => {
                    if ((e.key === "Enter" || e.key === ",") && cityInput.trim()) {
                      e.preventDefault();
                      const nx = cityInput.trim();
                      if (!(form.cities || []).includes(nx)) set("cities", [...(form.cities || []), nx]);
                      setCityInput("");
                    } else if (e.key === "Backspace" && !cityInput && (form.cities || []).length) {
                      set("cities", (form.cities || []).slice(0, -1));
                    }
                  }}
                  data-testid="plantilla-cities-input"
                />
              </div>
              <p className="text-[11px] text-muted-foreground">Enter o coma para añadir · Backspace para quitar la última.</p>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Descripción de venta (HTML)</Label>
              <Textarea rows={7} value={form.description || ""} onChange={(e) => set("description", e.target.value)} data-testid="plantilla-description-input" />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <div className="flex items-center justify-between">
                <Label>Imágenes del itinerario del cliente</Label>
                <div className="flex items-center gap-1.5">
                  <Button type="button" variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={() => setShowUrls((v) => !v)} data-testid="plantilla-toggle-urls">
                    <Link2 className="mr-1 h-3.5 w-3.5" />Pegar URLs
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => setPickerOpen(true)} data-testid="plantilla-add-images-btn">
                    <ImagePlus className="mr-1.5 h-4 w-4" />Añadir fotos
                  </Button>
                </div>
              </div>
              {dialogImages.length > 0 ? (
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-4" data-testid="plantilla-images-grid">
                  {dialogImages.map((u, i) => (
                    <div key={u} className="group relative aspect-[4/3] overflow-hidden rounded-md border bg-muted" data-testid={`plantilla-image-${i}`}>
                      <img src={imgSrc(u)} alt="" loading="lazy" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.opacity = 0.15; }} />
                      {i === 0 && <Badge className="absolute left-1 top-1 gap-1 bg-background/85 text-[9px] text-foreground"><Star className="h-2.5 w-2.5" />Portada</Badge>}
                      <div className="absolute inset-0 flex items-center justify-center gap-1.5 bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
                        {i !== 0 && <Button type="button" size="icon" variant="secondary" className="h-7 w-7" title="Portada" onClick={() => makeCover(u)} data-testid={`plantilla-image-cover-${i}`}><Star className="h-3.5 w-3.5" /></Button>}
                        <Button type="button" size="icon" variant="destructive" className="h-7 w-7" title="Quitar" onClick={() => removeImage(u)} data-testid={`plantilla-image-remove-${i}`}><X className="h-3.5 w-3.5" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <button type="button" onClick={() => setPickerOpen(true)} className="flex w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed py-8 text-muted-foreground transition-colors hover:border-primary hover:text-primary" data-testid="plantilla-images-empty">
                  <ImagePlus className="h-7 w-7" /><span className="text-sm">Añade fotos (Shutterstock, subir o banco). La 1ª es la portada.</span>
                </button>
              )}
              {showUrls && (
                <Textarea rows={3} placeholder="Una URL por línea" value={typeof form._imagesText === "string" ? form._imagesText : (form.images || []).join("\n")}
                  onChange={(e) => { set("_imagesText", e.target.value); set("images", e.target.value.split("\n").map((u) => u.trim()).filter(Boolean)); setImgIdx(0); }}
                  data-testid="plantilla-images-input" />
              )}
            </div>
          </div>
          <DialogFooter>
            {editing?.id && (
              <Button
                type="button"
                variant="outline"
                onClick={() => { setConvertCategory("city_guide"); setConvertOpen(true); }}
                data-testid="convert-to-editorial-button"
                className="mr-auto"
              >
                <BookOpen className="mr-1.5 h-4 w-4" />Convertir a Guía
              </Button>
            )}
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={() => saveMut.mutate()} disabled={!form.name || saveMut.isPending} data-testid="save-plantilla-button">{saveMut.isPending ? "Guardando…" : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImagePicker open={pickerOpen} onOpenChange={setPickerOpen} onSelect={addImage} multiple
        title="Añadir fotos a la plantilla" defaultQuery={form.location || form.name || ""} />

      {/* ─── Diálogo Auto-clasificación AI ─────────────────────────────── */}
      <Dialog open={classifyOpen} onOpenChange={setClassifyOpen}>
        <DialogContent className="max-w-2xl" data-testid="classify-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" /> Auto-clasificar plantillas con AI
            </DialogTitle>
            <DialogDescription>
              Claude Sonnet 4.6 leerá el nombre y descripción de cada plantilla sin clasificar
              y le asignará un <b>tipo de servicio</b> (uno de los 8) más las <b>ciudades</b> detectadas.
              El proceso corre en segundo plano y puedes cerrar esta ventana.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <div className="rounded-lg border p-2 text-center">
                <p className="text-lg font-bold tabular-nums">{meta.td_total ?? 0}</p>
                <p className="text-[10px] text-muted-foreground">TD totales</p>
              </div>
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-center">
                <p className="text-lg font-bold tabular-nums text-emerald-700">{Math.max(0, (meta.td_total ?? 0) - (meta.sin_clasificar ?? 0))}</p>
                <p className="text-[10px] text-emerald-700">Clasificadas</p>
              </div>
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-center">
                <p className="text-lg font-bold tabular-nums text-amber-700">{meta.sin_clasificar ?? 0}</p>
                <p className="text-[10px] text-amber-700">Sin clasificar</p>
              </div>
              <div className="rounded-lg border p-2 text-center">
                <p className="text-lg font-bold tabular-nums">{jobQ.data?.errors ?? 0}</p>
                <p className="text-[10px] text-muted-foreground">Errores</p>
              </div>
            </div>

            {jobRunning ? (
              <div className="space-y-2 rounded-lg border border-primary/40 bg-primary/5 p-3" data-testid="classify-progress-panel">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium text-primary">
                    <Sparkles className="mr-1 inline h-3.5 w-3.5 animate-pulse" />
                    Procesando… {jobQ.data?.processed ?? 0} / {jobQ.data?.total ?? 0}
                  </span>
                  <span className="text-muted-foreground">{jobQ.data?.updated ?? 0} actualizadas</span>
                </div>
                <Progress
                  value={jobQ.data?.total ? ((jobQ.data.processed / jobQ.data.total) * 100) : 0}
                  className="h-2"
                  data-testid="classify-progress-bar"
                />
                {jobQ.data?.last_error && (
                  <p className="text-[11px] text-destructive" data-testid="classify-last-error">Último error: {jobQ.data.last_error}</p>
                )}
              </div>
            ) : jobStatus === "done" ? (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="classify-done-message">
                Último batch completado · {jobQ.data?.processed ?? 0} plantillas procesadas ·
                {" "}{jobQ.data?.updated ?? 0} actualizadas.
              </div>
            ) : jobStatus === "error" ? (
              <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive" data-testid="classify-error-message">
                Error en el último batch: {jobQ.data?.error || "desconocido"}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Al pulsar <b>Iniciar</b> se lanza el batch para las plantillas seleccionadas
                {classifyAll
                  ? <>: <b>TODAS</b> las {meta.td_total ?? 0} plantillas TD (sobrescribirá clasificaciones previas).</>
                  : <>: solo las <b>sin clasificar</b> ({meta.sin_clasificar ?? 0} pendientes).</>}
                {" "}Duración estimada: ~1 min por cada 200 plantillas.
              </p>
            )}

            <div className="flex items-center justify-between rounded-lg border bg-muted/30 p-2.5" data-testid="classify-mode-toggle">
              <div className="space-y-0.5">
                <Label className="text-sm">Reclasificar TODAS las plantillas</Label>
                <p className="text-[11px] text-muted-foreground">
                  Si activas esto, se sobrescribirán las clasificaciones anteriores (útil si mejoras el prompt o quieres re-alinear).
                </p>
              </div>
              <Switch
                checked={classifyAll}
                onCheckedChange={setClassifyAll}
                disabled={jobRunning}
                data-testid="classify-all-switch"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setClassifyOpen(false)} data-testid="classify-cancel">Cerrar</Button>
            <Button
              onClick={() => classifyMut.mutate()}
              disabled={jobRunning || classifyMut.isPending || (!classifyAll && (meta.sin_clasificar ?? 0) === 0)}
              data-testid="classify-start-button"
            >
              <Sparkles className="mr-1.5 h-4 w-4" />
              {jobRunning
                ? "En curso…"
                : classifyAll
                  ? `Reclasificar todas (${meta.td_total ?? 0})`
                  : (meta.sin_clasificar === 0 ? "Todo clasificado" : `Iniciar (${meta.sin_clasificar ?? 0})`)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Diálogo Convertir a Guía editorial ────────────────────── */}
      <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
        <DialogContent className="max-w-md" data-testid="convert-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-indigo-600" />Convertir a Guía & Consejo
            </DialogTitle>
            <DialogDescription>
              La plantilla dejará de aparecer en Travel Documents y pasará al módulo
              <b> Guías & Consejos</b> con la categoría que elijas. Se eliminarán su
              <code className="mx-1 rounded bg-muted px-1 text-xs">service_type</code>
              y <code className="rounded bg-muted px-1 text-xs">cities</code>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Categoría editorial</Label>
            <Select value={convertCategory} onValueChange={setConvertCategory}>
              <SelectTrigger data-testid="convert-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {EDITORIAL_CATEGORIES.map((c) => (
                  <SelectItem key={c.key} value={c.key} data-testid={`convert-category-${c.key}`}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConvertOpen(false)}>Cancelar</Button>
            <Button
              onClick={() => convertMut.mutate()}
              disabled={convertMut.isPending}
              data-testid="convert-confirm-button"
            >
              <BookOpen className="mr-1.5 h-4 w-4" />
              {convertMut.isPending ? "Convirtiendo…" : "Convertir a Guía"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent data-testid="delete-plantilla-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar plantilla?</AlertDialogTitle>
            <AlertDialogDescription>Se eliminará “{delTarget?.name}”. Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => delMut.mutate(delTarget.id)} data-testid="confirm-delete-plantilla">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* AI Rewrite Transfers — modal (feb-2026 · C.4) */}
      <Dialog open={rewriterOpen} onOpenChange={setRewriterOpen}>
        <DialogContent className="max-w-6xl overflow-hidden p-0" data-testid="ai-rewriter-dialog">
          <div className="max-h-[85vh] overflow-y-auto">
            <AITransferRewriter />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
